import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/authStore";
import { useToast } from "@/hooks/use-toast";
import api, { getApiError } from "@/services/api";
import ConsentCheckboxes from "@/components/auth/ConsentCheckboxes";
import LoggedInJoinPanel from "@/components/company/LoggedInJoinPanel";

const registerSchema = z
  .object({
    name: z.string().min(2, "이름은 2자 이상이어야 합니다."),
    email: z.string().email("유효한 이메일을 입력해주세요."),
    phone: z.string().optional(),
    password: z.string().min(8, "비밀번호는 8자 이상이어야 합니다."),
    confirmPassword: z.string(),
    agreeToTerms: z.literal(true, {
      errorMap: () => ({ message: "이용약관에 동의해주세요." }),
    }),
    agreeToPrivacy: z.literal(true, {
      errorMap: () => ({ message: "개인정보 수집 및 이용에 동의해주세요." }),
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다.",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("company"); // 초대링크(?company=)로 전달된 회사 코드
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyCode, setCompanyCode] = useState<string | null>(null); // 검증 완료된 회사 코드
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  // 이메일 중복 실시간 검증 상태
  const [emailStatus, setEmailStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [emailTakenReason, setEmailTakenReason] = useState<string | null>(null);

  const { register: registerUser, isLoading, user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      agreeToTerms: false as any,
      agreeToPrivacy: false as any,
    },
  });

  const agreeToTerms = watch("agreeToTerms");
  const agreeToPrivacy = watch("agreeToPrivacy");
  const emailValue = watch("email");

  // 초대링크(?company=)의 회사 코드로 기업 정보 조회·검증.
  // 회사 코드는 사용자가 직접 입력하지 않고 초대링크로만 전달된다.
  // 검증에 성공하면 companyCode/companyName이 세팅되어 어느 회사에 가입하는지 표시된다.
  useEffect(() => {
    const code = (inviteCode ?? "").trim();

    if (!code) {
      setCompanyCode(null);
      setCompanyName(null);
      setCompanyLogo(null);
      setCompanyError(null);
      setIsLoadingCompany(false);
      return;
    }

    let cancelled = false;
    setIsLoadingCompany(true);
    setCompanyError(null);

    api
      .get("/auth/company-info", { params: { company_code: code } })
      .then((response) => {
        if (cancelled) return;
        setCompanyCode(code);
        setCompanyName(response.data.name);
        setCompanyLogo(response.data.logo_url); // API에서 전체 경로로 반환됨
        setCompanyError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setCompanyCode(null);
        setCompanyName(null);
        setCompanyLogo(null);
        setCompanyError("유효하지 않은 초대 링크입니다. 회사로부터 받은 링크를 확인해주세요.");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCompany(false);
      });

    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  // 이메일 중복 실시간 검증 (대상 회사 기준). 디바운스로 과도한 요청 방지.
  useEffect(() => {
    const email = (emailValue ?? "").trim();
    // 형식이 유효한 이메일일 때만 확인
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailStatus("idle");
      setEmailTakenReason(null);
      return;
    }

    let cancelled = false;
    setEmailStatus("checking");
    const timer = setTimeout(() => {
      api
        .get("/auth/check-email", {
          params: { email, company_code: companyCode || undefined },
        })
        .then((response) => {
          if (cancelled) return;
          if (response.data.available) {
            setEmailStatus("available");
            setEmailTakenReason(null);
          } else {
            setEmailStatus("taken");
            setEmailTakenReason(response.data.reason || "이미 등록된 이메일입니다.");
          }
        })
        .catch(() => {
          // 확인 실패 시엔 막지 않음(제출 시 백엔드가 최종 검증)
          if (!cancelled) {
            setEmailStatus("idle");
            setEmailTakenReason(null);
          }
        });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailValue, companyCode]);

  const onSubmit = async (data: RegisterFormData) => {
    // 초대링크로 들어왔는데 회사 검증이 안 된 경우(무효 링크) 제출 차단.
    // 초대코드가 없으면 개인 계정으로 가입(→ 로그인 시 로비에서 회사 등록/가입).
    const codeEntered = !!inviteCode?.trim();
    if (codeEntered && !companyCode) {
      toast({
        title: "초대 링크를 확인해주세요",
        description:
          "유효하지 않은 초대 링크입니다. 회사로부터 받은 링크를 다시 확인해주세요.",
        variant: "destructive",
      });
      return;
    }

    // 이메일 중복이 확인된 경우 제출 차단.
    if (emailStatus === "taken") {
      toast({
        title: "이미 등록된 이메일",
        description: emailTakenReason || "다른 이메일을 사용해주세요.",
        variant: "destructive",
      });
      return;
    }
    try {
      await registerUser({
        name: data.name,
        email: data.email,
        password: data.password,
        phone: data.phone,
        company_code: companyCode || undefined, // 기업 코드 전달
        agree_to_terms: true,
        agree_to_privacy: true,
      });

      // 성공 페이지 표시
      setRegistrationSuccess(true);
    } catch (error) {
      const apiError = getApiError(error);

      console.log('🔍 [RegisterPage] API Error:', apiError);
      console.log('🔍 [RegisterPage] Error code:', apiError.code);
      console.log('🔍 [RegisterPage] Error message:', apiError.message);

      // 에러 코드별 메시지 매핑
      let errorTitle = "회원가입 실패";
      let errorMessage = apiError.message;

      if (apiError.code === "EMAIL_EXISTS") {
        errorTitle = "이미 등록된 이메일";
        console.log('✅ [RegisterPage] EMAIL_EXISTS detected, using backend message');
        // 백엔드에서 보낸 메시지를 그대로 사용 (승인 대기 중/사용 중 구분됨)
        // errorMessage는 이미 apiError.message에서 가져온 값
      } else if (apiError.code === "COMPANY_NOT_FOUND") {
        errorTitle = "잘못된 기업 코드";
        errorMessage = "유효하지 않은 기업 코드입니다. URL을 확인해주세요.";
      } else if (apiError.code === "COMPANY_CODE_REQUIRED") {
        errorTitle = "회사 코드가 필요합니다";
        errorMessage =
          "기존 회사에 합류하려면 회사 코드가 필요합니다. 새 회사를 만들려면 '기업 회원가입'을 이용하세요.";
      } else if (apiError.message.includes("duplicate key")) {
        errorTitle = "중복된 정보";
        errorMessage = "이미 등록된 이메일 또는 정보입니다.";
      }

      console.log('🔍 [RegisterPage] Final title:', errorTitle);
      console.log('🔍 [RegisterPage] Final message:', errorMessage);

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // 로그인 상태에서 초대 링크(?company=)로 들어오면: 새 회원가입 폼 대신
  // '현재 계정으로 이 회사에 가입 신청' 확인 패널을 표시한다(별도 메뉴 없이 링크 접근 시점에 노출).
  // (user가 null인 no_company/로비 상태는 제외 — 실제 소속 계정만 대상)
  if (isAuthenticated && user && inviteCode) {
    return <LoggedInJoinPanel companyCode={inviteCode} />;
  }

  // 회원가입 성공 화면
  if (registrationSuccess) {
    return (
      <div className="max-w-md mx-auto text-center">
        <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
          <svg
            className="w-12 h-12 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h2 className="mb-4 text-2xl font-bold text-gray-900">
          {companyCode ? "직원 가입 신청이 완료되었습니다" : "회원가입이 완료되었습니다"}
        </h2>

        {/* 기업 정보 표시 */}
        {companyCode && companyName && (
          <div className="mb-6 p-4 bg-muted rounded-lg border inline-flex items-center gap-3">
            {companyLogo ? (
              <img
                src={companyLogo}
                alt={companyName}
                className="h-10 w-10 rounded object-contain bg-white p-1"
              />
            ) : (
              <Building2 className="h-10 w-10 text-primary p-2 bg-white rounded" />
            )}
            <p className="font-semibold text-lg">{companyName}</p>
          </div>
        )}

        {companyCode ? (
          <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200 text-left">
            <p className="mb-4 text-base text-gray-700">
              <strong className="text-blue-900">관리자 승인 후 로그인하실 수 있습니다.</strong>
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">✓</span>
                <span>승인이 완료되면 등록하신 이메일로 알림을 보내드립니다.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">✓</span>
                <span>승인 후 로그인하여 서비스를 이용하실 수 있습니다.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">✓</span>
                <span>승인 절차는 통상 1~2 영업일 소요됩니다.</span>
              </li>
            </ul>
          </div>
        ) : (
          <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200 text-left">
            <p className="mb-4 text-base text-gray-700">
              <strong className="text-blue-900">이제 로그인하실 수 있습니다.</strong>
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">✓</span>
                <span>로그인하면 <strong>새 회사를 만들거나</strong> 기존 회사에 가입 신청할 수 있습니다.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">✓</span>
                <span>회사를 만들거나 가입 신청하면 관리자 승인 후 이용할 수 있습니다.</span>
              </li>
            </ul>
          </div>
        )}

        <Button
          onClick={() => navigate("/login")}
          className="w-full"
          size="lg"
        >
          로그인 페이지로 이동
        </Button>

        <p className="mt-4 text-sm text-muted-foreground">
          {companyCode
            ? "승인이 완료되면 위 버튼을 눌러 로그인해주세요."
            : "위 버튼을 눌러 로그인 후 회사를 만들거나 가입 신청하세요."}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-2 text-2xl font-bold">회원가입</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        {inviteCode
          ? "아래 회사에 직원으로 가입 신청합니다. 관리자 승인 후 이용할 수 있습니다."
          : "개인 계정으로 가입하고, 로그인 후 회사를 만들거나 기존 회사에 가입 신청할 수 있습니다."}
      </p>

      {/* 가입 대상 회사: 초대링크(?company=)로 들어온 경우에만 회사명 표시.
          사용자가 회사 코드를 직접 입력하지 않는다(초대링크의 파라미터로만 전달). */}
      {inviteCode && (
        <div className="mb-6 space-y-2">
          {isLoadingCompany && (
            <p className="text-sm text-muted-foreground">회사 확인 중...</p>
          )}
          {companyError && (
            <p className="text-sm text-destructive">{companyError}</p>
          )}
          {companyCode && companyName && !isLoadingCompany && (
            <div className="p-3 bg-muted rounded-lg border flex w-fit mx-auto items-center gap-3">
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName}
                  className="h-10 w-10 rounded object-contain bg-white p-1"
                />
              ) : (
                <Building2 className="h-10 w-10 text-primary p-2 bg-white rounded" />
              )}
              <p className="font-semibold">{companyName}</p>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">이름 *</Label>
          <Input
            id="name"
            type="text"
            placeholder="홍길동"
            {...register("name")}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">이메일 *</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            {...register("email")}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
          {!errors.email && emailStatus === "checking" && (
            <p className="text-sm text-muted-foreground">이메일 확인 중...</p>
          )}
          {!errors.email && emailStatus === "taken" && (
            <p className="text-sm text-destructive">{emailTakenReason}</p>
          )}
          {!errors.email && emailStatus === "available" && (
            <p className="text-sm text-green-600">사용 가능한 이메일입니다.</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">연락처</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="010-1234-5678"
            {...register("phone")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">비밀번호 *</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">비밀번호 확인 *</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            {...register("confirmPassword")}
          />
          {errors.confirmPassword && (
            <p className="text-sm text-destructive">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <ConsentCheckboxes
          termsAgreed={agreeToTerms === true}
          privacyAgreed={agreeToPrivacy === true}
          onTermsChange={(checked) => setValue("agreeToTerms", checked as any, { shouldValidate: true })}
          onPrivacyChange={(checked) => setValue("agreeToPrivacy", checked as any, { shouldValidate: true })}
          errors={{
            terms: errors.agreeToTerms?.message,
            privacy: errors.agreeToPrivacy?.message,
          }}
        />

        <Button
          type="submit"
          className="w-full"
          disabled={
            isLoading ||
            (!!inviteCode?.trim() && !companyCode) ||
            emailStatus === "taken" ||
            emailStatus === "checking"
          }
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          회원가입 신청
        </Button>
      </form>

      <div className="mt-6 text-center text-sm">
        <span className="text-muted-foreground">이미 계정이 있으신가요? </span>
        <Link to="/login" className="text-primary hover:underline">
          로그인
        </Link>
      </div>

      <div className="mt-4 text-center text-sm border-t pt-4">
        <span className="text-muted-foreground">회사를 운영하시나요? </span>
        <Link to="/company-register" className="text-primary hover:underline font-semibold">
          새 회사 만들기 (기업 회원가입)
        </Link>
      </div>
    </div>
  );
}
